import type { BoardHold, Hold, RouteLine } from '../../../types/climb';
import { missingHoldRoles } from '../board-grid/roles';
import { missingPhotoHoldRoles } from '../photo-hold-roles';
import { isDrawableLine } from '../route-line';
import type { ClimbEditorRoute } from './useClimbEditorRoute';

/** The shortest a name may be and still be one. Mirrors the model's rule. */
export const MIN_NAME_LENGTH = 2;

export interface ClimbRequirementsInput {
  route: ClimbEditorRoute;
  name: string;
  gradeId: string;
  holdColor?: string;
  /** Whether there is a picture to draw on — a board always has one. */
  hasPhoto?: boolean;
  /** Circles on a photo (Boulder), the line (Route), grid cells (BoardClimb). */
  holds?: Hold[];
  line?: RouteLine | null;
  boardHolds?: BoardHold[];
}

/**
 * What a climb is still missing, as translation keys, in the order the form asks
 * for them.
 *
 * One function for all three types so the button, the page and anything added
 * later cannot drift into disagreeing about when a climb is finished. Empty
 * means finished.
 *
 * Whether anything short of that can still be *saved* is the caller's question:
 * a board problem cannot, while a climb on a photo may go up with its drawing
 * unfinished, once the setter has been asked — see `isDrawingAdvice`.
 *
 * The rule underneath is the same everywhere — a climb says where it begins,
 * what it is climbed on and where it ends — and only *how it says so* changes:
 * a board problem paints three roles on a grid, a boulder circles them on a
 * photo, a route draws one line from the first to the last. What differs by
 * place is whether there is a picture at all: see `photoRequired`.
 */
export function climbRequirements({
  route,
  name,
  gradeId,
  holdColor,
  hasPhoto = false,
  holds = [],
  line = null,
  boardHolds = [],
}: ClimbRequirementsInput): string[] {
  const missing: string[] = [];

  // Only where a name is what the climb is known by — see `nameRequired`.
  // Everywhere else it is asked for once the climb is already up, and a name
  // typed there still has to be a name.
  //
  // Which is the second clause: a field that is optional still cannot be saved
  // half typed. The model refuses a one-letter name (`minimum: 2, allow_blank`),
  // so the page says so where the letter was typed rather than letting the save
  // come back with it.
  const typedName = name.trim();
  const nameTooShort = typedName.length < MIN_NAME_LENGTH;
  if (route.nameRequired ? nameTooShort : nameTooShort && typedName.length > 0) {
    missing.push('climbEditor.missing.name');
  }
  if (!gradeId) missing.push('climbEditor.missing.grade');

  if (route.type === 'BoardClimb') {
    missing.push(...missingHoldRoles(boardHolds).map((role) => `climbEditor.missing.role.${role}`));
    return missing;
  }

  if (route.usesHoldColor && !holdColor?.trim()) missing.push('climbEditor.missing.holdColor');
  if (route.photoRequired && !hasPhoto) missing.push('climbEditor.missing.image');

  // Nothing drawn is only an answer where there was nothing to draw on. Once a
  // photo is in — even on a crag, which does not insist on one — leaving it
  // unmarked describes no climb, and the picture would say less than the name.
  if (!hasPhoto) return missing;

  if (route.type === 'Route') {
    if (!isDrawableLine(line)) missing.push('climbEditor.missing.line');
    return missing;
  }

  missing.push(...missingPhotoHoldRoles(holds).map((role) => `climbEditor.missing.role.${role}`));
  return missing;
}

/**
 * Which control answers a requirement — the one that goes red, and the one the
 * page scrolls to.
 *
 * Everything that is not a field of the form is the **drawing**: the photo
 * itself, the line, the roles a boulder or a board problem still wants. They are
 * all answered on the picture, and so they are all said in one place on it (see
 * `MissingHint`) rather than by a red mark on a control that does not exist.
 */
export type ClimbRequirementField = 'name' | 'grade' | 'holdColor' | 'drawing';

export function requirementField(key: string): ClimbRequirementField {
  if (key === 'climbEditor.missing.name') return 'name';
  if (key === 'climbEditor.missing.grade') return 'grade';
  if (key === 'climbEditor.missing.holdColor') return 'holdColor';
  return 'drawing';
}

/**
 * Whether a requirement of a climb **on a photo** is advice rather than a rule.
 *
 * What is drawn on the picture — the circles' roles, a route's line — is worth
 * having and not worth refusing the climb over. A setter putting up a wall of
 * twenty knows what they have just bolted on, and a problem whose start is not
 * circled is still a problem; a problem that never went up because the form
 * would not let it is nothing at all. So the caption goes on naming what is
 * missing, all along (see `MissingHint`), and the save puts it to the setter
 * once before going ahead without it (see `UnmarkedClimbDialog`) — it does not
 * stand in the way.
 *
 * Two things this deliberately does not loosen:
 *
 * * **the photo itself**, which is the frame every mark is stored against.
 *   Going up without one is a different decision, with a dialog of its own and
 *   a camera left in the picture's place (see `ClimbPhotoSource`);
 * * **a board problem's roles**, which are spelled with these same keys. There
 *   the three roles *are* the problem — the wall lights them and nothing else —
 *   so nothing is left of one drawn without them. Which is why this is a
 *   predicate the photo editor applies to its own list, and not a rule baked
 *   into `climbRequirements` for every kind of climb.
 */
export function isDrawingAdvice(key: string): boolean {
  return key === 'climbEditor.missing.line' || key.startsWith('climbEditor.missing.role.');
}

/**
 * The same requirement, named short, for the caption written across the foot of
 * the picture.
 *
 * The roles are called there what the legend calls them — "Start", "Top" — and
 * not what a sentence in a list would call them ("a start hold"). Three of those
 * spelled out is two lines of text over the wall being drawn on, and the words
 * on the legend are the ones the setter has just been picking from anyway.
 */
export function shortRequirementKey(key: string): string {
  const prefix = 'climbEditor.missing.role.';
  return key.startsWith(prefix) ? `climbs.holdRoles.${key.slice(prefix.length)}.label` : key;
}

/**
 * How a control says which requirement it answers. Read off the page rather than
 * held in a ref, because the three editors put these controls in different
 * places and only the page they are on knows where.
 */
export const REQUIREMENT_ATTRIBUTE = 'data-requirement';

/** Spread onto whatever element the setter should be looking at. */
export function requirementAnchor(field: ClimbRequirementField) {
  return { [REQUIREMENT_ATTRIBUTE]: field };
}

/**
 * Put the first thing still wanted on screen.
 *
 * First **on the page**, not first in the rules: the drawing is asked for last
 * and drawn at the top, and a setter told "something is missing" looks up from
 * where they are. Scrolling past the picture to the name field when neither has
 * been done would be answering a question they did not ask.
 */
export function scrollToFirstRequirement(fields: ClimbRequirementField[]): void {
  const nodes = fields
    .map((field) => document.querySelector(`[${REQUIREMENT_ATTRIBUTE}="${field}"]`))
    .filter((node): node is Element => node !== null);
  if (nodes.length === 0) return;

  const first = nodes.reduce((earliest, node) =>
    earliest.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING ? node : earliest
  );

  first.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
